import Image from "next/image";
import React, { useState, useEffect, useRef } from "react";
import { parseHtml } from "@/utils/common";

interface TimelineProps {
  items: any;
  activationOffset?: number;
  reverseLayout?: boolean;
}

const AnimatedTimeline: React.FC<TimelineProps> = ({
  items,
  activationOffset = 0,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  // Written straight to the DOM: as state it re-rendered every item on every scroll frame
  const lineRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timelineRef = useRef<HTMLDivElement>(null);

  const maxItems = items.length;
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  useEffect(() => {
    const handleScroll = () => {
      const viewportCenter = window.innerHeight / 2 + activationOffset;
      let currentActive = -1;
      let maxHeight = 0;

      itemRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const iconCenter = rect.top + 32;

          if (iconCenter <= viewportCenter) {
            currentActive = index;
            if (itemRefs.current[0]) {
              const currentIconTop = iconCenter;
              if (timelineRef.current) {
                const timelineRect =
                  timelineRef.current.getBoundingClientRect();
                if (index === maxItems - 1) {
                  const firstHeight =
                    itemRefs.current[0].getBoundingClientRect().height;
                  maxHeight =
                    currentIconTop - timelineRect.top + firstHeight - 30;
                } else {
                  maxHeight = currentIconTop - timelineRect.top + 120;
                }
              }
            }
          }
        }
      });

      setActiveIndex(currentActive);
      if (lineRef.current) lineRef.current.style.height = `${Math.max(0, maxHeight)}px`;
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationOffset]);

  return (
    <div>
      <div ref={timelineRef} className="relative w-full">
        {/* Center Line */}
        <div className="absolute md:left-1/2 left-[10px] transform -translate-x-1/2 top-0 bottom-0 w-0.5">
          <div className="absolute top-0 left-0 right-0 h-full bg-gray-500" />
          <div
            ref={lineRef}
            className="absolute top-0 left-0 right-0 bg-border-white h-full"
            style={{ height: 0 }}
          />
        </div>

        {items.map((item: any, index: number) => {
          const isActive = index === activeIndex;
          const isPassed = index <= activeIndex;
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [isHovered, setIsHovered] = useState(false);

          const defaultImage = baseUrl + item?.image?.data?.attributes?.url;
          const hoverImage = item?.hover_image?.data?.attributes?.url
            ? baseUrl + item?.hover_image?.data?.attributes?.url
            : "/images/verify.svg";

          // show hover image either when hovered OR when active via scroll
          const showHoverImage = isHovered || isActive;

          return (
            <div
              key={item.id}
              ref={(el: HTMLDivElement | null) => {
                if (el !== null) itemRefs.current[index] = el;
              }}
              className={`relative w-full pt-[60px] flex items-start md:${
                index % 2 === 0 ? "justify-start" : "justify-end"
              }`}
            >
              <div className="flex items-start w-[96%] ml-auto md:ml-0 md:w-1/2">
                {/* Card */}
                <div
                  className={`w-full transition-all z-20 duration-500 ${
                    isActive || isPassed ? "opacity-100" : "opacity-40"
                  } ${index % 2 === 0 ? "order-1" : "order-2"}`}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <div
                    className={`relative w-[100%] h-auto z-10 p-6 border transition-all duration-300 ${
                      isActive ? "border-[#fff]" : "border-[#EDEDEE4D]"
                    }`}
                  >
                    {isActive && (
                      <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-50 pointer-events-none"
                        style={{
                          backgroundImage: "url('/images/bf-process.png')",
                        }}
                      />
                    )}

                    {/* Image reacts to hover and scroll */}
                    {item?.image?.data?.attributes?.url &&
                      item?.hover_image?.data?.attributes?.url && (
                        <Image
                          height={48}
                          width={48}
                          alt="icon"
                          className="mb-[16px] transition-all duration-300"
                          src={showHoverImage ? hoverImage : defaultImage}
                        />
                      )}

                    <h3
                      className={`text-heading-6 font-bold mb-[16px] ${
                        isActive || isPassed ? "text-[#fff]" : "text-[#83858C]"
                      }`}
                    >
                      {item.title}
                    </h3>

                    <span
                      className={`text-subtitle-3 font-normal ${
                        isActive ? "text-[#fff]" : "text-[#83858C]"
                      }`}
                    >
                      {item.date}
                    </span>

                    {/* div, not p: the CMS description already contains <p> tags. */}
                    <div
                      className={`text-subtitle-3 font-normal ${
                        isActive || isPassed ? "text-[#fff]" : "text-[#83858C]"
                      }`}
                    >
                      {parseHtml(item.description)}
                    </div>
                  </div>
                </div>

                {/* Step Circle */}
                <div
                  className={`
                    relative w-[52px] h-[52px] flex items-center justify-center border-2 font-semibold text-subtitle-1 
                    ${
                      index % 2 === 0
                        ? "before:left-full md:before:left-[-28px] -translate-x-[26px] md:translate-x-[26px] md:order-2 order-[-1]"
                        : "before:left-full -translate-x-[26px] order-1"
                    }
                    before:w-[28px] before:h-[1px] before:absolute before:z-0 before:content-[''] before:bg-[#EDEDEE4D]
                    ${
                      isActive || isPassed
                        ? "bg-[#000] border-[#fff] text-[#fff] before:bg-[#fff]"
                        : "bg-[#000] border-[#EDEDEE4D] text-[#83858C] before:bg-[#83858C]"
                    }`}
                >
                  {index + 1}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnimatedTimeline;
